import { useMemo } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { topics } from "@/mocks/topics";
import { categories } from "@/mocks/categories";
import usePageSeo from "@/hooks/usePageSeo";
import Breadcrumbs from "@/components/feature/Breadcrumbs";
import TopicRankItem from "@/components/feature/TopicRankItem";
import { formatCount } from "@/components/feature/TopicCard";

const ORIGIN = "https://ftosc.cn";

export default function TopicsPage() {
  const [searchParams, setSearchParams] = useSearchParams();
  const activeCategory = searchParams.get("category") ?? "all";

  const ranked = useMemo(() => [...topics].sort((a, b) => b.heat - a.heat), []);

  const filtered = useMemo(
    () => (activeCategory === "all" ? ranked : ranked.filter((t) => t.categoryId === activeCategory)),
    [ranked, activeCategory]
  );

  const currentCategory = categories.find((c) => c.id === activeCategory);

  usePageSeo({
    title: `${currentCategory ? currentCategory.name : "全部分类"}话题榜 - 瓜田吃瓜热门话题排行`,
    description: currentCategory
      ? `瓜田吃瓜${currentCategory.name}话题榜，汇总该分区讨论度最高的热门话题与事件脉络，${currentCategory.desc}`
      : "瓜田吃瓜话题榜，按阅读量、讨论量与观点分歧综合排序，每小时更新，抢先了解当下最受关注的热门话题。",
    keywords: `瓜田吃瓜话题榜,${currentCategory ? currentCategory.name : "热门话题"},瓜田吃瓜热门话题,话题排行`,
    path: activeCategory === "all" ? "/topics" : `/topics?category=${activeCategory}`,
    jsonLd: [
      {
        "@context": "https://schema.org",
        "@type": "CollectionPage",
        name: `${currentCategory ? currentCategory.name : "全部"}话题榜 - 瓜田吃瓜`,
        url: `${ORIGIN}/topics`,
        inLanguage: "zh-CN",
        description: "按讨论热度排序的瓜田吃瓜话题榜单，涵盖综艺、影视、音乐、演出等分区。",
      },
      {
        "@context": "https://schema.org",
        "@type": "BreadcrumbList",
        itemListElement: [
          { "@type": "ListItem", position: 1, name: "首页", item: `${ORIGIN}/` },
          { "@type": "ListItem", position: 2, name: "话题榜", item: `${ORIGIN}/topics` },
        ],
      },
      {
        "@context": "https://schema.org",
        "@type": "ItemList",
        name: `${currentCategory ? currentCategory.name : "全部"}话题排行`,
        itemListElement: filtered.slice(0, 10).map((topic, index) => ({
          "@type": "ListItem",
          position: index + 1,
          name: topic.title,
          url: `${ORIGIN}/topic/${topic.id}`,
        })),
      },
    ],
  });

  const handleSelect = (id: string) => {
    if (id === "all") {
      setSearchParams({});
      return;
    }
    setSearchParams({ category: id });
  };

  return (
    <div className="w-full">
      <section className="w-full bg-background-200/50 border-b border-background-200/70">
        <div className="w-full px-4 md:px-8 py-8 md:py-12">
          <Breadcrumbs items={[{ label: "话题榜" }]} />
          <div className="mt-6 max-w-3xl">
            <span className="inline-flex items-center gap-2 text-xs tracking-[0.22em] uppercase text-primary-600 mb-3">
              <span className="w-6 h-px bg-primary-400" />
              Ranking
            </span>
            <h1 className="text-3xl md:text-5xl font-bold text-foreground-950 leading-tight">
              瓜田吃瓜话题榜
            </h1>
            <p className="mt-4 text-sm md:text-base leading-relaxed text-foreground-600">
              综合阅读量、讨论回复量与观点分歧程度排序，每小时更新一次。点击任意话题即可查看事件时间线与讨论区的主要交锋点。
            </p>
          </div>
        </div>
      </section>

      <section className="w-full px-4 md:px-8 py-8 md:py-12">
        <div className="flex flex-wrap gap-2 md:gap-3">
          <button
            type="button"
            onClick={() => handleSelect("all")}
            className={`px-4 py-2 rounded-full text-sm whitespace-nowrap border transition-colors duration-200 ${
              activeCategory === "all"
                ? "bg-primary-500 text-background-50 border-primary-500"
                : "bg-background-50 text-foreground-600 border-background-200 hover:border-primary-300 hover:text-primary-700"
            }`}
          >
            全部分类
          </button>
          {categories.map((category) => (
            <button
              key={category.id}
              type="button"
              onClick={() => handleSelect(category.id)}
              className={`px-4 py-2 rounded-full text-sm whitespace-nowrap border transition-colors duration-200 ${
                activeCategory === category.id
                  ? "bg-primary-500 text-background-50 border-primary-500"
                  : "bg-background-50 text-foreground-600 border-background-200 hover:border-primary-300 hover:text-primary-700"
              }`}
            >
              {category.name}
            </button>
          ))}
        </div>

        <div className="mt-8 grid grid-cols-1 lg:grid-cols-3 gap-8 lg:gap-12">
          <div className="lg:col-span-2">
            <div className="flex items-center justify-between pb-4 border-b border-background-200/80">
              <h2 className="text-lg md:text-xl font-bold text-foreground-950">
                {currentCategory ? `${currentCategory.name} · 共 ${filtered.length} 个话题` : `全部话题 · 共 ${filtered.length} 个`}
              </h2>
              <span className="text-xs text-foreground-500 whitespace-nowrap">按热度排序</span>
            </div>

            {filtered.length > 0 ? (
              <div className="mt-2">
                {filtered.map((topic, index) => (
                  <TopicRankItem key={topic.id} topic={topic} rank={index + 1} />
                ))}
              </div>
            ) : (
              <div className="mt-8 rounded-lg border border-background-200/80 bg-background-50 p-10 text-center">
                <i className="ri-inbox-line text-3xl text-foreground-400" />
                <p className="mt-3 text-sm text-foreground-600">该分类暂时还没有话题，去讨论区提议题吧。</p>
                <Link
                  to="/discussion"
                  className="mt-5 inline-flex items-center gap-1.5 px-5 py-2.5 rounded-full bg-primary-500 text-background-50 text-sm hover:bg-primary-600 transition-colors"
                >
                  去讨论区提议题
                </Link>
              </div>
            )}
          </div>

          <aside className="flex flex-col gap-6">
            <div className="rounded-lg bg-background-200/50 border border-background-200/70 p-5">
              <h3 className="text-base font-bold text-foreground-950">榜单是怎么排的</h3>
              <ul className="mt-4 flex flex-col gap-3">
                {[
                  { icon: "ri-eye-line", text: "站内阅读量与完读表现" },
                  { icon: "ri-chat-3-line", text: "讨论区回复数量与活跃时长" },
                  { icon: "ri-arrow-left-right-line", text: "观点分歧程度与立场数量" },
                  { icon: "ri-line-chart-line", text: "近期热度变化与新增话题" },
                ].map((item) => (
                  <li key={item.text} className="flex items-start gap-3 text-sm text-foreground-700">
                    <span className="shrink-0 w-8 h-8 flex items-center justify-center rounded-lg bg-background-50 text-primary-600">
                      <i className={item.icon} />
                    </span>
                    {item.text}
                  </li>
                ))}
              </ul>
            </div>

            <div className="rounded-lg bg-background-200/50 border border-background-200/70 p-5">
              <h3 className="text-base font-bold text-foreground-950">分类速览</h3>
              <div className="mt-4 flex flex-col gap-2">
                {categories.map((category) => (
                  <button
                    key={category.id}
                    type="button"
                    onClick={() => handleSelect(category.id)}
                    className="flex items-center justify-between gap-3 px-3 py-2.5 rounded-lg bg-background-50 border border-background-200/70 hover:border-accent-300 transition-colors text-left"
                  >
                    <span className="flex items-center gap-2.5 text-sm text-foreground-800">
                      <i className={`${category.icon} text-accent-700`} />
                      {category.name}
                    </span>
                    <span className="text-xs text-foreground-500 whitespace-nowrap">
                      {formatCount(category.count)}
                    </span>
                  </button>
                ))}
              </div>
            </div>
          </aside>
        </div>
      </section>
    </div>
  );
}